import * as amplitude from "@amplitude/analytics-browser";
import type {
  UserJobType,
  UserLevelType,
} from "@redotech/account-common/job-level";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoadWithInput } from "@redotech/react-util/load";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { putUserProfile } from "@redotech/redo-merchant-app-common/client/user";
import {
  ReloadUserContext,
  UserContext,
} from "@redotech/redo-merchant-app-common/user";
import { memo, useContext, useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { FirstInstallOnboardingPendingContext } from "../../../onboarding/first-install-redirect-guard";
import { JOB_LEVEL_BACKFILL_MODAL_ACTIVE_BODY_ATTRIBUTE } from "./constants";
import { shouldShowJobLevelBackfillPrompt } from "./eligibility";
import { JobLevelBackfillModal } from "./job-level-backfill-modal";

interface SaveInput {
  job: UserJobType[];
  level: UserLevelType;
}

export const JobLevelBackfillPromptHost = memo(
  function JobLevelBackfillPromptHost() {
    const auth = useContext(AuthContext);
    const user = useContext(UserContext);
    const reloadUser = useContext(ReloadUserContext);
    const client = useRequiredContext(RedoMerchantClientContext);
    const location = useLocation();
    const firstInstallOnboardingPending = useContext(
      FirstInstallOnboardingPendingContext,
    );
    const loggedShownUserId = useRef<string | null>(null);

    const visible = shouldShowJobLevelBackfillPrompt({
      firstInstallOnboardingPending,
      isAdminImpersonation: auth?.isAdminImpersonation,
      user,
      pathname: location.pathname,
    });

    useJobLevelBackfillExclusiveLayer(visible);

    useEffect(() => {
      if (!visible || !user || loggedShownUserId.current === user._id) {
        return;
      }
      amplitude.logEvent("job_level_backfill_shown", { userId: user._id });
      loggedShownUserId.current = user._id;
    }, [user, visible]);

    const [saveLoad, save] = useTriggerLoadWithInput(
      async (input: SaveInput, signal: AbortSignal) => {
        if (!user) {
          return false;
        }
        await putUserProfile(client, {
          user: { ...user, jobProfile: { job: input.job, level: input.level } },
          signal,
        });
        if (signal.aborted) {
          return false;
        }
        amplitude.logEvent("job_level_backfill_completed", {
          userId: user._id,
        });
        reloadUser?.();
        return true;
      },
    );

    if (!visible || !user) {
      return null;
    }

    return (
      <JobLevelBackfillModal
        error={!!saveLoad.error}
        initialJob={user.jobProfile?.job}
        initialLevel={user.jobProfile?.level ?? null}
        onSave={(value) => save(value)}
        saving={saveLoad.pending}
      />
    );
  },
);

// Mirrors the reclaim install modal's exclusive layer: while the backfill modal
// is visible, a body attribute drives global CSS that hides every other overlay
// so this required prompt owns the screen.
function useJobLevelBackfillExclusiveLayer(visible: boolean) {
  useLayoutEffect(() => {
    if (!visible) {
      return;
    }
    document.body.setAttribute(
      JOB_LEVEL_BACKFILL_MODAL_ACTIVE_BODY_ATTRIBUTE,
      "true",
    );
    return () => {
      if (
        document.body.getAttribute(
          JOB_LEVEL_BACKFILL_MODAL_ACTIVE_BODY_ATTRIBUTE,
        ) === "true"
      ) {
        document.body.removeAttribute(
          JOB_LEVEL_BACKFILL_MODAL_ACTIVE_BODY_ATTRIBUTE,
        );
      }
    };
  }, [visible]);
}
